import { Component, OnDestroy, OnInit } from '@angular/core';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { CounsellingPhotoGalleryComponent } from '../../../modules/counseling/counselling-photo-gallery/counselling-photo-gallery.component';
import { EducationPhotoGalleryComponent } from '../education-reports/education-presentation/education-photo-gallery/education-photo-gallery.component';
import { CollageEditorComponent, CollageEditorResult, COLLAGE_EDITOR_DIALOG_CONFIG, COLLAGE_FULL_PAGE_HEIGHT_PX } from './collage-editor/collage-editor.component';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { CentreService } from '../../../services/centre.service';
import { LoadingService } from '../../../services/loading.service';
import {
  NarrativeImpactReportService,
  CountDatum,
  BeneficiaryStats,
  ProgressSubjectScore,
  CopingSegment,
  CaseStudyData,
  CoverPhoto,
  DepartmentImpactReport,
  LibraryPhoto,
  PlaceholderPhoto,
  ReportSections,
  uniqueStrings,
  htmlToLines,
  educationReportPhotos,
  counsellingReportPhotos,
} from '../../../services/narrative-impact-report.service';
import { NarrativeImpactReportAiService } from '../../../services/narrative-impact-report-ai.service';
import { Centre } from '../../../interfaces/Centres';
import { FullChild } from '../../../interfaces/FullChild';

interface CaseStudyForm extends CaseStudyData {
  backgroundNotes: string;
  generating: boolean;
  uploadingPhoto: boolean;
  childCode?: string;
  photoIsSuggested?: boolean;
}

const DEFAULT_INTRO_TEXT = 'Non-formal education intervention at AccessLife Assistance Foundation was initiated with the vision of bridging the gap and helping children get in touch with their education. So that after successfully completing their treatment children can have a co-relation with their ongoing studies despite the lapse during treatment. The baseline assessment, intermediate assessment, discharge assessment and IEP goals are being planned and implemented by our teachers.';
const DEFAULT_AGE_RANGE_TEXT = 'Our intervention caters the children from the age group of 04 years to 14 years.';
const DEFAULT_TODDLER_TEXT = 'Toddlers are handled by our volunteers under the guidance of our teachers. Volunteers take care of various activities which are conducted for toddlers on a day-to-day basis. They overall take care of Gross Motor and Fine Motor skills.';
const DEFAULT_PROGRESS_NOTE = 'Note: The graphical representation is the Average Score of the percentage our children scored in Assessments.';
const DEFAULT_COUNSELLING_QUOTE = 'Emotional Well-being is just as important to us as Breathing is!';
const DEFAULT_COUNSELLING_INTRO = [
  'With this thought, Access life Assistance Foundation began its Psychological Counseling Support for its Beneficiaries in 2020.',
  'The objective of the psychological interventions done at Access life is focused on assisting the family and the child to cope with the stress of the diagnosis, treatment & prognosis of the illness while adjusting to living with other families in a new city. Towards this, the assessments and interventions are being planned and implemented by our psychologists.',
];
const DEFAULT_INTERVENTIONS_TEXT = 'Beneficiaries were educated about the importance of mental health and how counselling can positively impact daily life and caregiving. Techniques such as positive self-talk, affirmation sheets, and open-ended questioning were used to encourage emotional expression. Evidence-based approaches, including Cognitive Behavioural Therapy (CBT), Rational Emotive Behaviour Therapy (REBT), Solution-Focused Brief Therapy (SFBT), and Acceptance and Commitment Therapy (ACT) was employed to address negative thought patterns and promote solution-oriented thinking.';
const DEFAULT_INTERPRETATION_BULLETS = [
  'Frequency of low coping: The number of circumstances beneficiaries reported being stressed and unable to cope',
  'Frequency of Moderate coping: The number of circumstances beneficiaries reported stress and inability to cope half of the times',
  'Frequency of High coping: A number of circumstances beneficiaries reported no stress and were able to cope',
];

// Default headings/labels for the Education and Counselling sections of the report — the same
// defaults as EDU_HEADINGS / CSL_HEADINGS in functions/src/narrative-impact-report.ts, shown in
// the wizard so they can be edited. A field cleared to blank falls back to the default there.
const DEFAULT_EDU_HEADINGS = {
  title: 'Non-Formal Education Impact Report',
  teacherCol: 'Teacher Session',
  volunteerCol: 'Volunteer Sessions',
  specialDaysCol: 'Special Days Observed',
  totalCol: 'Total Sessions',
  highlightsHeading: 'Highlights:',
  progressChartTitle: 'Progress Chart',
  sessionGallery: 'Educational Sessions Images',
  activityGallery: 'Educational Activities on Important Day Images',
  volunteerGallery: 'Education Volunteer Activities',
};
const DEFAULT_CSL_HEADINGS = {
  title: 'Counselling Impact Report',
  individualCol: 'Individual Sessions',
  interventionsCol: 'Psychological interventions',
  groupCol: 'Group Sessions',
  totalCol: 'Total Sessions',
  highlightsHeading: 'Highlights:',
  problemsAddressedLabel: 'Problems Addressed in Counselling',
  interventionsToolsLabel: 'Interventions and Tools Used -',
  admissionChartTitle: 'Admission Psychological Assessment',
  dischargeChartTitle: 'Discharge Psychological Assessment',
  interpretationHeading: 'Interpretation from Graph:',
  sessionGallery: 'Counselling Session Images',
  groupGallery: 'Group Counselling Session Images',
};

@Component({
  selector: 'app-narrative-impact-report',
  templateUrl: './narrative-impact-report.component.html',
  styleUrls: ['./narrative-impact-report.component.scss'],
})
export class NarrativeImpactReportComponent implements OnInit, OnDestroy {
  centres$: Observable<Centre[]>;
  years: number[] = [];
  reportId: string | null = null;

  // Step 1 – Config
  configForm: FormGroup;
  quarterOptions: { label: string; fullLabel: string; start: Date; end: Date }[] = [];
  coverPhotoUrl = '';
  contactPhotoUrl = '';
  uploadingCoverPhoto = false;
  uploadingContactPhoto = false;
  children$: Observable<FullChild[]> = of([]);
  coverPhotoLibrary$: Observable<CoverPhoto[]> = of([]);
  contactPhotoLibrary$: Observable<LibraryPhoto[]> = of([]);
  coverPhotoChildCode = '';
  coverPhotoChildName = '';
  private logoDataUrl = '';
  private autoCoverTitle = '';

  // Placeholder image pool — decorative photos not tied to any centre/quarter. When a photo
  // gallery page's own content ends with a large blank gap, the generated report drops one of
  // these in to fill it (see the placeholder-fill script in functions/src/narrative-impact-report.ts).
  placeholderLibrary$: Observable<PlaceholderPhoto[]> = of([]);
  selectedPlaceholderPhotos: string[] = [];
  // Set once the user ticks any placeholder on/off, which stops ngOnInit's "select the whole
  // library" default from overwriting their choice on the next Firestore emission (uploading a
  // new placeholder re-emits the collection).
  private placeholderSelectionTouched = false;
  uploadingPlaceholder = false;

  // Step 2 – Beneficiary Stats & Charts
  statsForm: FormGroup;
  ageWiseData: CountDatum[] = [];
  genderWiseData: CountDatum[] = [];
  hospitalWiseData: CountDatum[] = [];
  beneficiaryRemarks = '';
  fetchingStats = false;

  // Step 3 – Education Impact
  eduForm: FormGroup;
  educationHighlights: string[] = [];
  educationChildNotes = '';
  generatingEduHighlights = false;
  fetchingEduReports = false;
  eduImpactReports: DepartmentImpactReport[] = [];
  eduSessionPhotos: string[] = [];
  eduActivityPhotos: string[] = [];
  eduVolunteerPhotos: string[] = [];
  uploadingEduSession = false;
  uploadingEduActivity = false;
  uploadingEduVolunteer = false;
  // Optional Fabric.js-generated collage image, one per gallery. When set, the
  // backend renders this single image instead of laying out `*Photos` itself.
  eduSessionCollageUrl = '';
  eduActivityCollageUrl = '';
  eduVolunteerCollageUrl = '';
  // True once the exported collage is tall enough (>= full-page preset height) that it
  // should get a dedicated full-height report page instead of flowing inline.
  eduSessionCollageFullPage = false;
  eduActivityCollageFullPage = false;
  eduVolunteerCollageFullPage = false;

  // Step 4 – Counselling Impact
  cslForm: FormGroup;
  counsellingHighlights: string[] = [];
  counsellingConcernNotes = '';
  problemsAddressedParagraph = '';
  generatingCslHighlights = false;
  generatingProblemsParagraph = false;
  admissionCoping: CopingSegment[] = [
    { label: 'Able to Cope up', value: 0 },
    { label: 'Sometimes able to', value: 0 },
    { label: 'Difficulty in Coping', value: 0 },
  ];
  dischargeCoping: CopingSegment[] = [
    { label: 'Able to Cope up', value: 0 },
    { label: 'Sometimes able to', value: 0 },
    { label: 'Difficulty in Coping', value: 0 },
  ];
  fetchingCslReports = false;
  cslImpactReports: DepartmentImpactReport[] = [];
  cslSessionPhotos: string[] = [];
  cslGroupPhotos: string[] = [];
  uploadingCslSession = false;
  uploadingCslGroup = false;
  // Optional Fabric.js-generated collage, same convention as the eduXxxCollageUrl fields.
  cslSessionCollageUrl = '';
  cslGroupCollageUrl = '';
  cslSessionCollageFullPage = false;
  cslGroupCollageFullPage = false;

  // Step 5 – Case Studies
  caseStudies: CaseStudyForm[] = [];

  // Step 6 – Generate PDF
  sections: ReportSections = {
    cover: true, beneficiary: true, education: true, counselling: true, caseStudies: true, contact: true,
  };
  readonly sectionOptions: { key: keyof ReportSections; label: string }[] = [
    { key: 'cover', label: 'Cover Page' },
    { key: 'beneficiary', label: 'Beneficiary Data' },
    { key: 'education', label: 'Education Impact' },
    { key: 'counselling', label: 'Counselling Impact' },
    { key: 'caseStudies', label: 'Case Studies' },
    { key: 'contact', label: 'Contact Page' },
  ];
  generatingPdf = false;
  saving = false;
  savedAt: Date | null = null;
  previewHtml = '';

  private subs = new SubSink();

  constructor(
    private fb: FormBuilder,
    private centreService: CentreService,
    private loader: LoadingService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private functions: AngularFireFunctions,
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
    private reportService: NarrativeImpactReportService,
    private aiService: NarrativeImpactReportAiService,
    private route: ActivatedRoute,
  ) {
    this.centres$ = this.centreService.getAllCentres();

    // Financial years (April–March), stored as the FY's start year: 2026 means FY 2026-2027. The
    // list advances on its own each April — previous, current and next FY.
    const currentFy = this.reportService.financialYearStart();
    this.years = [currentFy - 1, currentFy, currentFy + 1];
    this.quarterOptions = this.reportService.getQuartersForFinancialYear(currentFy);

    this.configForm = this.fb.group({
      centreCode: ['', Validators.required],
      centreName: [''],
      year: [currentFy, Validators.required],
      quarterLabel: ['', Validators.required],
      coverTitle: [''],
      watermarkStyle: ['wave'],
    });

    this.subs.add(
      this.configForm.get('quarterLabel')!.valueChanges.subscribe((label: string) => {
        const nextAuto = label ? `${label} REPORT` : '';
        const current = this.configForm.value.coverTitle;
        if (!current || current === this.autoCoverTitle) {
          this.configForm.patchValue({ coverTitle: nextAuto }, { emitEvent: false });
        }
        this.autoCoverTitle = nextAuto;
      })
    );

    this.statsForm = this.fb.group({
      bedOccupancyPercent: [0, [Validators.required, Validators.min(0)]],
      admissionCount: [0, [Validators.required, Validators.min(0)]],
      caregiverAccommodationCount: [0, [Validators.required, Validators.min(0)]],
      educationSessionCount: [0, [Validators.required, Validators.min(0)]],
      counsellingSessionCount: [0, [Validators.required, Validators.min(0)]],
    });

    this.eduForm = this.fb.group({
      introText: [DEFAULT_INTRO_TEXT],
      ageRangeText: [DEFAULT_AGE_RANGE_TEXT],
      toddlerText: [DEFAULT_TODDLER_TEXT],
      teacherSessions: [0, [Validators.required, Validators.min(0)]],
      volunteerSessions: [0, [Validators.required, Validators.min(0)]],
      specialDaysObserved: [0, [Validators.required, Validators.min(0)]],
      langBaseline: [0], langDischarge: [0],
      mathsBaseline: [0], mathsDischarge: [0],
      evsBaseline: [0], evsDischarge: [0],
      progressNote: [DEFAULT_PROGRESS_NOTE],
      langLabel: ['Language'],
      mathsLabel: ['Maths'],
      evsLabel: ['EVS/GK'],
      headings: this.fb.group(DEFAULT_EDU_HEADINGS),
    });

    this.cslForm = this.fb.group({
      openingQuote: [DEFAULT_COUNSELLING_QUOTE],
      introParagraphs: [DEFAULT_COUNSELLING_INTRO.join('\n\n')],
      individualSessions: [0, [Validators.required, Validators.min(0)]],
      psychologicalInterventions: [0, [Validators.required, Validators.min(0)]],
      groupSessions: [0, [Validators.required, Validators.min(0)]],
      interventionsToolsParagraph: [DEFAULT_INTERVENTIONS_TEXT],
      admissionsDischargeParagraphs: [''],
      closingParagraph: [''],
      interpretationBullets: [DEFAULT_INTERPRETATION_BULLETS.join('\n')],
      headings: this.fb.group(DEFAULT_CSL_HEADINGS),
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadReport(id);

    this.coverPhotoLibrary$ = this.reportService.getCoverPhotos();
    this.contactPhotoLibrary$ = this.reportService.getContactPhotos();
    // Default to the WHOLE library selected. The report's placeholder-fill script is only
    // emitted at all when at least one placeholder URL is passed, so an empty selection
    // silently disables gap-filling across the entire report — an easy trap, since nothing
    // in the output hints that the feature was switched off. Selecting everything by default
    // means fills just work; ticking a photo off still takes over completely (see
    // placeholderSelectionTouched in togglePlaceholderPhoto).
    this.placeholderLibrary$ = this.reportService.getPlaceholderPhotos().pipe(
      tap(photos => {
        if (!this.placeholderSelectionTouched) {
          this.selectedPlaceholderPhotos = photos.map(p => p.photoUrl);
        }
      }),
    );
    this.loadLogoDataUrl();
  }

  // The generated report HTML is opened later (print preview, or a saved draft reopened on another
  // day/host) outside of this page's lifetime, so embedding the logo as a data URI — instead of the
  // previous `window.location.origin + '/assets/logo.png'` absolute URL — means it always renders
  // even if that origin/session is long gone, rather than showing a broken-image icon.
  //
  // The transparent-background logo (logo-removebg.png) comes first: assets/logo.png has a solid
  // white background that shows as a white box over the report's grey corner watermark. logo.png
  // is the fallback if the transparent file is missing. "Missing" has to mean "not an image", not
  // just a failed status: Firebase Hosting rewrites unknown paths to index.html with a 200.
  private async loadLogoDataUrl(): Promise<void> {
    for (const path of ['/assets/logo-removebg.png', '/assets/logo.png']) {
      try {
        const response = await fetch(path);
        const blob = await response.blob();
        if (!response.ok || !blob.type.startsWith('image/')) continue;
        this.logoDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        return;
      } catch {
        // try the next candidate
      }
    }
    this.logoDataUrl = '';
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get selectedQuarter() {
    return this.quarterOptions.find(q => q.label === this.configForm.value.quarterLabel);
  }

  get periodLabel(): string {
    return this.selectedQuarter?.fullLabel || '';
  }

  onYearChange(): void {
    this.quarterOptions = this.reportService.getQuartersForFinancialYear(this.configForm.value.year);
    this.configForm.patchValue({ quarterLabel: '' });
  }

  // ── Reopen a saved report (Edit on the report list) ─────────────────────────

  private async loadReport(id: string): Promise<void> {
    this.loader.setLoading(true);
    try {
      const r: any = await this.reportService.getReport(id);
      if (!r) {
        this.snackbar.open('Report not found', '', { duration: 3000, panelClass: 'error' });
        return;
      }
      this.reportId = id;

      // The financial year isn't stored; it follows from the quarter's start date.
      const from: Date | null = r.from?.toDate ? r.from.toDate() : (r.from ? new Date(r.from) : null);
      const year = from ? this.reportService.financialYearStart(from) : this.configForm.value.year;
      this.quarterOptions = this.reportService.getQuartersForFinancialYear(year);
      this.configForm.patchValue({
        centreCode: r.centreCode || '',
        centreName: r.centreName || '',
        year,
        quarterLabel: r.quarterLabel || '',
        watermarkStyle: r.watermarkStyle || 'wave',
      });
      // After the quarter, which would otherwise set its automatic cover title.
      this.configForm.patchValue({ coverTitle: r.coverTitle || '' });
      if (r.centreCode) this.children$ = this.centreService.getChildrenByCentre(r.centreCode);

      this.coverPhotoUrl = r.coverPhotoUrl || '';
      this.contactPhotoUrl = r.contactPhotoUrl || '';
      if (Array.isArray(r.placeholderPhotos)) {
        this.placeholderSelectionTouched = true;
        this.selectedPlaceholderPhotos = [...r.placeholderPhotos];
      }
      if (r.sections) this.sections = { ...this.sections, ...r.sections };

      if (r.beneficiaryStats) this.statsForm.patchValue(r.beneficiaryStats);
      this.ageWiseData = r.ageWiseData || [];
      this.genderWiseData = r.genderWiseData || [];
      this.hospitalWiseData = r.hospitalWiseData || [];
      this.beneficiaryRemarks = r.beneficiaryRemarks || '';
      // The saved numbers may have been corrected by hand: don't let entering the stats step
      // re-fetch and overwrite them.
      this.lastStatsKey = `${r.centreCode}|${r.quarterLabel}|${year}`;

      const edu = r.education || {};
      const chart: any[] = edu.progressChart || [];
      this.eduForm.patchValue({
        introText: edu.introText ?? DEFAULT_INTRO_TEXT,
        ageRangeText: edu.ageRangeText ?? DEFAULT_AGE_RANGE_TEXT,
        toddlerText: edu.toddlerText ?? DEFAULT_TODDLER_TEXT,
        teacherSessions: edu.teacherSessions ?? 0,
        volunteerSessions: edu.volunteerSessions ?? 0,
        specialDaysObserved: edu.specialDaysObserved ?? 0,
        langLabel: chart[0]?.subject || 'Language', langBaseline: chart[0]?.baseline ?? 0, langDischarge: chart[0]?.discharge ?? 0,
        mathsLabel: chart[1]?.subject || 'Maths', mathsBaseline: chart[1]?.baseline ?? 0, mathsDischarge: chart[1]?.discharge ?? 0,
        evsLabel: chart[2]?.subject || 'EVS/GK', evsBaseline: chart[2]?.baseline ?? 0, evsDischarge: chart[2]?.discharge ?? 0,
        progressNote: edu.progressNote ?? DEFAULT_PROGRESS_NOTE,
        headings: { ...DEFAULT_EDU_HEADINGS, ...(edu.headings || {}) },
      });
      this.educationHighlights = [...(edu.highlights || [])];
      this.eduSessionPhotos = [...(edu.sessionPhotos || [])];
      this.eduActivityPhotos = [...(edu.activityPhotos || [])];
      this.eduVolunteerPhotos = [...(edu.volunteerPhotos || [])];
      this.eduSessionCollageUrl = edu.sessionCollageUrl || '';
      this.eduActivityCollageUrl = edu.activityCollageUrl || '';
      this.eduVolunteerCollageUrl = edu.volunteerCollageUrl || '';
      this.eduSessionCollageFullPage = !!edu.sessionCollageFullPage;
      this.eduActivityCollageFullPage = !!edu.activityCollageFullPage;
      this.eduVolunteerCollageFullPage = !!edu.volunteerCollageFullPage;

      const csl = r.counselling || {};
      this.cslForm.patchValue({
        openingQuote: csl.openingQuote ?? DEFAULT_COUNSELLING_QUOTE,
        introParagraphs: (csl.introParagraphs || DEFAULT_COUNSELLING_INTRO).join('\n\n'),
        individualSessions: csl.individualSessions ?? 0,
        psychologicalInterventions: csl.psychologicalInterventions ?? 0,
        groupSessions: csl.groupSessions ?? 0,
        interventionsToolsParagraph: csl.interventionsToolsParagraph ?? DEFAULT_INTERVENTIONS_TEXT,
        admissionsDischargeParagraphs: (csl.admissionsDischargeParagraphs || []).join('\n\n'),
        closingParagraph: csl.closingParagraph || '',
        interpretationBullets: (csl.interpretationBullets || DEFAULT_INTERPRETATION_BULLETS).join('\n'),
        headings: { ...DEFAULT_CSL_HEADINGS, ...(csl.headings || {}) },
      });
      this.problemsAddressedParagraph = csl.problemsAddressedParagraph || '';
      this.counsellingHighlights = [...(csl.highlights || [])];
      if (csl.admissionCoping?.length) this.admissionCoping = csl.admissionCoping;
      if (csl.dischargeCoping?.length) this.dischargeCoping = csl.dischargeCoping;
      this.cslSessionPhotos = [...(csl.sessionPhotos || [])];
      this.cslGroupPhotos = [...(csl.groupPhotos || [])];
      this.cslSessionCollageUrl = csl.sessionCollageUrl || '';
      this.cslGroupCollageUrl = csl.groupCollageUrl || '';
      this.cslSessionCollageFullPage = !!csl.sessionCollageFullPage;
      this.cslGroupCollageFullPage = !!csl.groupCollageFullPage;

      this.caseStudies = (r.caseStudies || []).map((cs: any) => ({
        braveheartLabel: cs.braveheartLabel || '', name: cs.name || '', gender: cs.gender || '',
        age: cs.age || 0, dob: cs.dob || '', city: cs.city || '', state: cs.state || '',
        diagnosis: cs.diagnosis || '', hospital: cs.hospital || '', photo: cs.photo || '',
        paragraphs: Array.isArray(cs.paragraphs) ? [...cs.paragraphs] : [],
        backgroundNotes: '', generating: false, uploadingPhoto: false,
        childCode: cs.childCode || '', photoIsSuggested: false,
      }));

      const saved = r.updatedAt || r.createdAt;
      this.savedAt = saved?.toDate ? saved.toDate() : null;
    } catch (err) {
      console.error(err);
      this.snackbar.open('Could not load the saved report', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.loader.setLoading(false);
    }
  }

  onCentreChange(event: any): void {
    this.subs.add(
      this.centres$.subscribe(centres => {
        const c = centres.find(x => x.centreCode === event.value);
        if (c) this.configForm.patchValue({ centreName: c.centreName });
      })
    );

    this.children$ = this.centreService.getChildrenByCentre(event.value);
    this.coverPhotoChildCode = '';
    this.coverPhotoChildName = '';
  }

  selectCoverPhotoFromGallery(url: string): void {
    this.coverPhotoUrl = url;
  }

  onCoverPhotoChildSelect(event: any): void {
    const child: FullChild = event.value;
    if (!child) {
      this.coverPhotoChildCode = '';
      this.coverPhotoChildName = '';
      return;
    }
    this.coverPhotoChildCode = child.childCode;
    this.coverPhotoChildName = `${child.firstName || ''} ${child.lastName || ''}`.trim();
  }

  async removeCoverPhoto(photo: CoverPhoto): Promise<void> {
    if (!photo.id) return;
    await this.reportService.deleteCoverPhoto(photo.id);
    if (this.coverPhotoUrl === photo.photoUrl) this.coverPhotoUrl = '';
  }

  isCoverPhotoInLibrary(libraryPhotos: CoverPhoto[], url: string): boolean {
    return libraryPhotos.some(cp => cp.photoUrl === url);
  }

  private getChildPhotoUrl(admissionDocuments: { description: string; fileUrl: string }[]): string {
    const doc = admissionDocuments?.find(d => d.description === 'Family Photo');
    return doc ? doc.fileUrl : '';
  }

  async uploadCoverPhoto(event: any): Promise<void> {
    const file: File = event.target.files?.[0];
    if (!file) return;
    this.uploadingCoverPhoto = true;
    try {
      const urls = await this.uploadPhotosBatch([file], `narrative-impact-reports/${this.getWorkingReportId()}/cover`);
      this.coverPhotoUrl = urls[0];
      await this.reportService.addCoverPhoto(
        this.coverPhotoUrl,
        this.currentUserIdentity(),
        this.coverPhotoChildCode,
        this.coverPhotoChildName
      );
    } finally {
      this.uploadingCoverPhoto = false;
      event.target.value = '';
    }
  }

  private currentUserIdentity(): string {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return userData?.email || userData?.userName || userData?.displayName || '';
  }

  async uploadContactPhoto(event: any): Promise<void> {
    const file: File = event.target.files?.[0];
    if (!file) return;
    this.uploadingContactPhoto = true;
    try {
      const urls = await this.uploadPhotosBatch([file], `narrative-impact-reports/${this.getWorkingReportId()}/contact`);
      this.contactPhotoUrl = urls[0];
      await this.reportService.addContactPhoto(this.contactPhotoUrl, this.currentUserIdentity());
    } finally {
      this.uploadingContactPhoto = false;
      event.target.value = '';
    }
  }

  selectContactPhotoFromGallery(url: string): void {
    this.contactPhotoUrl = url;
  }

  async removeContactPhoto(photo: LibraryPhoto): Promise<void> {
    if (!photo.id) return;
    await this.reportService.deleteContactPhoto(photo.id);
    if (this.contactPhotoUrl === photo.photoUrl) this.contactPhotoUrl = '';
  }

  isPhotoInLibrary(libraryPhotos: LibraryPhoto[], url: string): boolean {
    return libraryPhotos.some(p => p.photoUrl === url);
  }

  // ── Placeholder image library (fills blank gallery-page space) ─────────────

  async uploadPlaceholderPhotos(event: any): Promise<void> {
    const files: FileList = event.target.files;
    if (!files?.length) return;
    this.uploadingPlaceholder = true;
    try {
      const urls = await this.uploadPhotosBatch(Array.from(files), 'narrative-impact-reports/placeholder-library');
      for (const url of urls) {
        await this.reportService.addPlaceholderPhoto(url, this.currentUserIdentity());
      }
    } catch {
      this.snackbar.open('Placeholder image upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.uploadingPlaceholder = false;
      event.target.value = '';
    }
  }

  togglePlaceholderPhoto(url: string): void {
    this.placeholderSelectionTouched = true;
    const i = this.selectedPlaceholderPhotos.indexOf(url);
    if (i >= 0) this.selectedPlaceholderPhotos.splice(i, 1);
    else this.selectedPlaceholderPhotos.push(url);
  }

  async removePlaceholderPhoto(photo: PlaceholderPhoto): Promise<void> {
    if (!photo.id) return;
    await this.reportService.deletePlaceholderPhoto(photo.id);
    this.selectedPlaceholderPhotos = this.selectedPlaceholderPhotos.filter(u => u !== photo.photoUrl);
  }

  // ── Step 2: Beneficiary Stats & Charts ─────────────────────────────────────

  // Stats are fetched automatically on entering the Statistics step, and again only when the
  // centre or period has changed since the last fetch, so edits made in the step aren't
  // overwritten just by stepping back and forth.
  private lastStatsKey = '';

  onStepChange(event: StepperSelectionEvent): void {
    if (event.selectedIndex !== 1) return;
    const key = `${this.configForm.value.centreCode}|${this.configForm.value.quarterLabel}|${this.configForm.value.year}`;
    if (key === this.lastStatsKey || this.fetchingStats) return;
    this.fetchStats();
  }

  async fetchStats(): Promise<void> {
    if (this.configForm.invalid) {
      this.snackbar.open('Please complete Step 1 first', '', { duration: 3000 });
      return;
    }
    const q = this.selectedQuarter;
    if (!q) return;

    const centreCode = this.configForm.value.centreCode;
    this.fetchingStats = true;
    this.loader.setLoading(true);

    try {
      const centreName = this.configForm.value.centreName || '';
      // Read Firestore afresh rather than reusing records loaded for an earlier fetch.
      this.reportService.clearFetchedData();
      // Each part is applied on its own, so one source failing (e.g. a missing index) doesn't
      // leave the rest of the step empty.
      const [beneficiary, age, gender, hospital, edu, progress, csl, coping] = await Promise.allSettled([
        this.reportService.getBeneficiaryStats(centreCode, q.start, q.end, centreName),
        this.reportService.getAgeWiseData(centreCode, q.start, q.end),
        this.reportService.getGenderWiseData(centreCode, q.start, q.end),
        this.reportService.getHospitalWiseData(centreCode, q.start, q.end),
        this.reportService.getEducationSessionCounts(centreCode, q.start, q.end, centreName),
        this.reportService.getEducationProgressChart(centreCode, q.start, q.end, centreName),
        this.reportService.getCounsellingSessionCounts(centreCode, q.start, q.end),
        this.reportService.getCopingCharts(centreCode, q.start, q.end),
      ]);
      const failed: string[] = [];
      const ok = <T>(res: PromiseSettledResult<T>, name: string): T | null => {
        if (res.status === 'fulfilled') return res.value;
        console.error(`Narrative report: fetching ${name} failed`, res.reason);
        failed.push(name);
        return null;
      };

      const b = ok(beneficiary, 'beneficiary stats');
      if (b) this.statsForm.patchValue(b as BeneficiaryStats);
      const ageData = ok(age, 'age chart');
      if (ageData) this.ageWiseData = ageData;
      const genderData = ok(gender, 'gender chart');
      if (genderData) this.genderWiseData = genderData;
      const hospitalData = ok(hospital, 'hospital chart');
      if (hospitalData) this.hospitalWiseData = hospitalData;

      const e = ok(edu, 'education sessions');
      if (e) this.eduForm.patchValue(e);
      const scores = ok(progress, 'education scores');
      if (scores) {
        this.eduForm.patchValue({
          langBaseline: scores[0]?.baseline || 0, langDischarge: scores[0]?.discharge || 0,
          mathsBaseline: scores[1]?.baseline || 0, mathsDischarge: scores[1]?.discharge || 0,
          evsBaseline: scores[2]?.baseline || 0, evsDischarge: scores[2]?.discharge || 0,
        });
      }

      const c = ok(csl, 'counselling sessions');
      if (c) this.cslForm.patchValue(c);
      const cope = ok(coping, 'coping charts');
      if (cope) {
        this.admissionCoping = cope.admission;
        this.dischargeCoping = cope.discharge;
      }

      if (failed.length) {
        this.snackbar.open(`Could not fetch: ${failed.join(', ')}. Enter those by hand or try again.`, 'OK',
          { duration: 9000, panelClass: 'error' });
      } else {
        // Only a complete fetch counts as done; a partial one is retried next time the step opens.
        this.lastStatsKey = `${centreCode}|${this.configForm.value.quarterLabel}|${this.configForm.value.year}`;
        this.snackbar.open('Stats fetched for ' + q.label, '', { duration: 3000 });
      }
    } catch (err) {
      console.error(err);
      this.snackbar.open('Error fetching data. Check console.', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.fetchingStats = false;
      this.loader.setLoading(false);
    }
  }

  addCountRow(list: CountDatum[]): void {
    list.push({ label: '', count: 0 });
  }

  removeCountRow(list: CountDatum[], i: number): void {
    list.splice(i, 1);
  }

  // ── Step 3: Education Impact ───────────────────────────────────────────────

  async generateEducationHighlights(): Promise<void> {
    this.generatingEduHighlights = true;
    try {
      const sv = this.eduForm.value;
      this.educationHighlights = await this.aiService.generateEducationHighlights({
        centreName: this.configForm.value.centreName || this.configForm.value.centreCode,
        periodLabel: this.periodLabel,
        teacherSessions: sv.teacherSessions,
        volunteerSessions: sv.volunteerSessions,
        specialDaysObserved: sv.specialDaysObserved,
        childNotes: this.educationChildNotes,
      });
    } catch (err: any) {
      // Show the reason (e.g. the AI service being busy) rather than a bare "failed".
      this.snackbar.open(`Highlight generation failed: ${err?.message || 'please try again'}`, 'OK', { duration: 7000, panelClass: 'error' });
    } finally {
      this.generatingEduHighlights = false;
    }
  }

  /** Pulls the Education department's already-saved impact report(s) for the selected centre and
   *  quarter into this step: highlights and the three photo galleries. Where the quarter has a
   *  quarterly report only that is used, otherwise all of its (monthly) reports are combined. */
  async fetchExistingEducationReports(): Promise<void> {
    const q = this.selectedQuarter;
    const centreCode = this.configForm.value.centreCode;
    if (!q || !centreCode) { this.snackbar.open('Select centre and quarter first', '', { duration: 3000 }); return; }
    this.fetchingEduReports = true;
    try {
      const found = await this.reportService.getEducationImpactReports(centreCode, q.start, q.end);
      this.eduImpactReports = found;
      if (!found.length) {
        this.snackbar.open(`No Education impact report found for ${q.label}`, '', { duration: 4000 });
        return;
      }
      const reports = this.preferQuarterlyReports(found);

      const highlights = uniqueStrings(reports.flatMap(r => htmlToLines(r.highlights)));
      if (highlights.length) this.educationHighlights = highlights;

      const photos = (key: string) => educationReportPhotos(reports, key);
      const session = photos('education'), activity = photos('celebration'), volunteer = photos('volunteering');
      if (session.length) this.eduSessionPhotos = session;
      if (activity.length) this.eduActivityPhotos = activity;
      if (volunteer.length) this.eduVolunteerPhotos = volunteer;

      // Session counts: a monthly report's teacher/volunteer counts are split into an individual
      // and a group figure (teacherSessions / teacherSessionsGroup) — the narrative report shows
      // one combined number per role, so the two are added back together. Several monthly reports
      // making up the quarter are summed; a single quarterly report is used as-is.
      const num = (v: any) => Number(v) || 0;
      const statsPatch: any = {
        teacherSessions: reports.reduce((sum, r) => sum + num(r['teacherSessions']) + num(r['teacherSessionsGroup']), 0),
        volunteerSessions: reports.reduce((sum, r) => sum + num(r['volunteerSessions']) + num(r['volunteerSessionsGroup']), 0),
        specialDaysObserved: reports.reduce((sum, r) => sum + num(r['daysObserved']), 0),
      };
      // Baseline/discharge Language, Maths, EVS-GK scores, in that order (see educationGraph() /
      // baselineArray in impact-reports.component.ts). Averaged across the reports making up the
      // quarter rather than summed, since these are score percentages, not counts.
      const scoreFields: [string, string][] = [['langBaseline', 'langDischarge'], ['mathsBaseline', 'mathsDischarge'], ['evsBaseline', 'evsDischarge']];
      scoreFields.forEach(([baseKey, dischargeKey], i) => {
        const baselines = reports.map(r => num(r['baseline']?.[i])).filter(v => v > 0);
        const discharges = reports.map(r => num(r['discharge']?.[i])).filter(v => v > 0);
        if (baselines.length) statsPatch[baseKey] = Math.round(baselines.reduce((a, b) => a + b, 0) / baselines.length);
        if (discharges.length) statsPatch[dischargeKey] = Math.round(discharges.reduce((a, b) => a + b, 0) / discharges.length);
      });
      this.eduForm.patchValue(statsPatch);

      this.snackbar.open(`Loaded ${reports.length} Education impact report(s) for ${q.label}`, '', { duration: 3000 });
    } catch (err) {
      console.error(err);
      this.snackbar.open('Could not fetch Education impact reports', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.fetchingEduReports = false;
    }
  }

  /** Same as {@link fetchExistingEducationReports}, for the Counselling department: its narrative
   *  sections, highlights and gallery photos. */
  async fetchExistingCounsellingReports(): Promise<void> {
    const q = this.selectedQuarter;
    const centreCode = this.configForm.value.centreCode;
    if (!q || !centreCode) { this.snackbar.open('Select centre and quarter first', '', { duration: 3000 }); return; }
    this.fetchingCslReports = true;
    try {
      const found = await this.reportService.getCounsellingImpactReports(centreCode, q.start, q.end);
      this.cslImpactReports = found;
      if (!found.length) {
        this.snackbar.open(`No Counselling impact report found for ${q.label}`, '', { duration: 4000 });
        return;
      }
      const reports = this.preferQuarterlyReports(found);
      const joined = (field: string) => uniqueStrings(reports.map(r => String(r[field] ?? '').trim())).join('\n\n');

      const problems = joined('problemAddressed');
      if (problems) this.problemsAddressedParagraph = problems;

      const highlights = uniqueStrings(reports.flatMap(r =>
        [r['counselling'], r['groupCounselling']].flatMap(t => htmlToLines(t))));
      if (highlights.length) this.counsellingHighlights = highlights;

      const patch: any = {};
      const tools = joined('intervention');
      if (tools) patch.interventionsToolsParagraph = tools;
      const admissionsDischarge = uniqueStrings(reports.flatMap(r => [r['para1'], r['para2']])
        .map(t => String(t ?? '').trim())).join('\n\n');
      if (admissionsDischarge) patch.admissionsDischargeParagraphs = admissionsDischarge;
      const closing = uniqueStrings(reports.flatMap(r => [r['endLine1'], r['endLine']])
        .map(t => String(t ?? '').trim())).join('\n\n');
      if (closing) patch.closingParagraph = closing;
      this.cslForm.patchValue(patch);

      const photos = counsellingReportPhotos(reports);
      if (photos.length) this.cslSessionPhotos = photos;

      // Session counts, saved per report under sessionCounts{individualSessions, groupSessions,
      // assessmentSessions} — assessmentSessions is that screen's count of admission/discharge
      // assessments, the same thing the narrative report calls "Psychological interventions".
      // Several monthly reports making up the quarter are summed.
      const num = (v: any) => Number(v) || 0;
      const sc = (r: DepartmentImpactReport, key: string) => num(r['sessionCounts']?.[key]);
      if (reports.some(r => r['sessionCounts'])) {
        this.cslForm.patchValue({
          individualSessions: reports.reduce((sum, r) => sum + sc(r, 'individualSessions'), 0),
          groupSessions: reports.reduce((sum, r) => sum + sc(r, 'groupSessions'), 0),
          psychologicalInterventions: reports.reduce((sum, r) => sum + sc(r, 'assessmentSessions'), 0),
        });
      }

      this.snackbar.open(`Loaded ${reports.length} Counselling impact report(s) for ${q.label}`, '', { duration: 3000 });
    } catch (err) {
      console.error(err);
      this.snackbar.open('Could not fetch Counselling impact reports', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.fetchingCslReports = false;
    }
  }

  /** "Jul 2026" for a one-month report, "1 Jul – 30 Sep 2026" otherwise; for the report list UI. */
  impactReportRangeLabel(r: DepartmentImpactReport): string {
    const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) => d.toLocaleDateString('en-IN', opts);
    const sameMonth = r.from.getMonth() === r.to.getMonth() && r.from.getFullYear() === r.to.getFullYear();
    return sameMonth
      ? fmt(r.from, { month: 'short', year: 'numeric' })
      : `${fmt(r.from, { day: 'numeric', month: 'short' })} – ${fmt(r.to, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }

  // A report spanning about a quarter (or more) supersedes the monthly reports inside it.
  private preferQuarterlyReports(reports: DepartmentImpactReport[]): DepartmentImpactReport[] {
    const QUARTER_MIN_DAYS = 80;
    const quarterly = reports.filter(r => (r.to.getTime() - r.from.getTime()) / 86400000 >= QUARTER_MIN_DAYS);
    return quarterly.length ? quarterly : reports;
  }

  addEduHighlight(): void { this.educationHighlights.push(''); }
  removeEduHighlight(i: number): void { this.educationHighlights.splice(i, 1); }

  async uploadEduPhotos(event: any, target: string): Promise<void> {
    const files: FileList = event.target.files;
    if (!files?.length) return;
    const flagKey = target === 'session' ? 'uploadingEduSession' : target === 'activity' ? 'uploadingEduActivity' : 'uploadingEduVolunteer';
    (this as any)[flagKey] = true;
    try {
      const urls = await this.uploadPhotosBatch(Array.from(files), `narrative-impact-reports/${this.getWorkingReportId()}/education-${target}`);
      if (target === 'session') this.eduSessionPhotos = [...this.eduSessionPhotos, ...urls];
      else if (target === 'activity') this.eduActivityPhotos = [...this.eduActivityPhotos, ...urls];
      else this.eduVolunteerPhotos = [...this.eduVolunteerPhotos, ...urls];
    } catch {
      this.snackbar.open('Photo upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      (this as any)[flagKey] = false;
      event.target.value = '';
    }
  }

  openEduGallery(target: string): void {
    const q = this.selectedQuarter;
    if (!q) { this.snackbar.open('Select centre and quarter first', '', { duration: 3000 }); return; }
    const ref = this.dialog.open(EducationPhotoGalleryComponent, {
      width: '90vw', maxWidth: '1400px', height: '90vh',
      data: { centreCode: this.configForm.value.centreCode, startDate: q.start, endDate: q.end },
    });
    ref.afterClosed().subscribe((docs: any[]) => {
      if (!docs?.length) return;
      const urls = docs.map((d: any) => d.url);
      if (target === 'session') this.eduSessionPhotos = [...this.eduSessionPhotos, ...urls];
      else if (target === 'activity') this.eduActivityPhotos = [...this.eduActivityPhotos, ...urls];
      else this.eduVolunteerPhotos = [...this.eduVolunteerPhotos, ...urls];
    });
  }

  removeEduPhoto(target: string, i: number): void {
    if (target === 'session') this.eduSessionPhotos.splice(i, 1);
    else if (target === 'activity') this.eduActivityPhotos.splice(i, 1);
    else this.eduVolunteerPhotos.splice(i, 1);
  }

  /** Opens the Fabric.js collage editor for one education photo gallery
   *  (session/activity/volunteer). Sketch: replicate this call + result
   *  handling for the Counselling galleries and Case Study photos once the
   *  layout templates and sizing UX are validated here. */
  openEduCollageEditor(target: string, sectionLabel: string): void {
    const urls = target === 'session' ? this.eduSessionPhotos
      : target === 'activity' ? this.eduActivityPhotos
      : this.eduVolunteerPhotos;
    if (urls.length < 2) {
      this.snackbar.open('Add at least 2 photos before building a collage', '', { duration: 3000 });
      return;
    }
    const ref = this.dialog.open(CollageEditorComponent, {
      ...COLLAGE_EDITOR_DIALOG_CONFIG,
      data: {
        photoUrls: urls,
        sectionLabel,
        // Session Photos renders first among the education galleries — default it to a
        // full report page so the section opens with a full-bleed image page, per design.
        defaultHeightPx: target === 'session' ? COLLAGE_FULL_PAGE_HEIGHT_PX : undefined,
      },
    });
    ref.afterClosed().subscribe((result: CollageEditorResult | undefined) => {
      if (!result) return;
      // Exported at (or above) the "Use Full Page Height" preset — give it a whole page.
      const fullPage = result.heightPx >= COLLAGE_FULL_PAGE_HEIGHT_PX;
      if (target === 'session') { this.eduSessionCollageUrl = result.dataUrl; this.eduSessionCollageFullPage = fullPage; }
      else if (target === 'activity') { this.eduActivityCollageUrl = result.dataUrl; this.eduActivityCollageFullPage = fullPage; }
      else { this.eduVolunteerCollageUrl = result.dataUrl; this.eduVolunteerCollageFullPage = fullPage; }
    });
  }

  // ── Step 4: Counselling Impact ─────────────────────────────────────────────

  async generateCounsellingHighlights(): Promise<void> {
    this.generatingCslHighlights = true;
    try {
      const sv = this.cslForm.value;
      this.counsellingHighlights = await this.aiService.generateCounsellingHighlights({
        centreName: this.configForm.value.centreName || this.configForm.value.centreCode,
        periodLabel: this.periodLabel,
        individualSessions: sv.individualSessions,
        groupSessions: sv.groupSessions,
        concernNotes: this.counsellingConcernNotes,
      });
    } catch (err: any) {
      // Show the reason (e.g. the AI service being busy) rather than a bare "failed".
      this.snackbar.open(`Highlight generation failed: ${err?.message || 'please try again'}`, 'OK', { duration: 7000, panelClass: 'error' });
    } finally {
      this.generatingCslHighlights = false;
    }
  }

  async generateProblemsAddressed(): Promise<void> {
    this.generatingProblemsParagraph = true;
    try {
      const sv = this.cslForm.value;
      this.problemsAddressedParagraph = await this.aiService.generateProblemsAddressedParagraph({
        centreName: this.configForm.value.centreName || this.configForm.value.centreCode,
        periodLabel: this.periodLabel,
        individualSessions: sv.individualSessions,
        groupSessions: sv.groupSessions,
        concernNotes: this.counsellingConcernNotes,
      });
    } catch (err: any) {
      // Show the reason (e.g. the AI service being busy) rather than a bare "failed".
      this.snackbar.open(`Paragraph generation failed: ${err?.message || 'please try again'}`, 'OK', { duration: 7000, panelClass: 'error' });
    } finally {
      this.generatingProblemsParagraph = false;
    }
  }

  addCslHighlight(): void { this.counsellingHighlights.push(''); }
  removeCslHighlight(i: number): void { this.counsellingHighlights.splice(i, 1); }

  async uploadCslPhotos(event: any, target: string): Promise<void> {
    const files: FileList = event.target.files;
    if (!files?.length) return;
    const flagKey = target === 'session' ? 'uploadingCslSession' : 'uploadingCslGroup';
    (this as any)[flagKey] = true;
    try {
      const urls = await this.uploadPhotosBatch(Array.from(files), `narrative-impact-reports/${this.getWorkingReportId()}/counselling-${target}`);
      if (target === 'session') this.cslSessionPhotos = [...this.cslSessionPhotos, ...urls];
      else this.cslGroupPhotos = [...this.cslGroupPhotos, ...urls];
    } catch {
      this.snackbar.open('Photo upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      (this as any)[flagKey] = false;
      event.target.value = '';
    }
  }

  openCslGallery(target: string): void {
    const q = this.selectedQuarter;
    if (!q) { this.snackbar.open('Select centre and quarter first', '', { duration: 3000 }); return; }
    const ref = this.dialog.open(CounsellingPhotoGalleryComponent, {
      width: '90vw', maxWidth: '1400px', height: '90vh',
      // The gallery's full-bleed dialog styles are scoped to this pane class.
      panelClass: 'counselling-photo-gallery-dialog-container',
      data: {
        centreCode: this.configForm.value.centreCode, startDate: q.start, endDate: q.end,
        sessionType: target === 'group' ? 'group' : 'individual',
      },
    });
    ref.afterClosed().subscribe((docs: any[]) => {
      if (!docs?.length) return;
      const urls = docs.map((d: any) => d.url);
      if (target === 'session') this.cslSessionPhotos = [...this.cslSessionPhotos, ...urls];
      else this.cslGroupPhotos = [...this.cslGroupPhotos, ...urls];
    });
  }

  removeCslPhoto(target: string, i: number): void {
    if (target === 'session') this.cslSessionPhotos.splice(i, 1);
    else this.cslGroupPhotos.splice(i, 1);
  }

  /** Opens the Fabric.js collage editor for one counselling photo gallery
   *  (session/group) — mirrors openEduCollageEditor() above. */
  openCslCollageEditor(target: string, sectionLabel: string): void {
    const urls = target === 'session' ? this.cslSessionPhotos : this.cslGroupPhotos;
    if (urls.length < 2) {
      this.snackbar.open('Add at least 2 photos before building a collage', '', { duration: 3000 });
      return;
    }
    const ref = this.dialog.open(CollageEditorComponent, {
      ...COLLAGE_EDITOR_DIALOG_CONFIG,
      data: { photoUrls: urls, sectionLabel },
    });
    ref.afterClosed().subscribe((result: CollageEditorResult | undefined) => {
      if (!result) return;
      const fullPage = result.heightPx >= COLLAGE_FULL_PAGE_HEIGHT_PX;
      if (target === 'session') { this.cslSessionCollageUrl = result.dataUrl; this.cslSessionCollageFullPage = fullPage; }
      else { this.cslGroupCollageUrl = result.dataUrl; this.cslGroupCollageFullPage = fullPage; }
    });
  }

  // ── Step 5: Case Studies ───────────────────────────────────────────────────

  addCaseStudy(): void {
    this.caseStudies.push({
      braveheartLabel: '', name: '', gender: '', age: 0, dob: '', city: '', state: '',
      diagnosis: '', hospital: '', photo: '', paragraphs: [],
      backgroundNotes: '', generating: false, uploadingPhoto: false,
      childCode: '', photoIsSuggested: false,
    });
  }

  removeCaseStudy(i: number): void {
    this.caseStudies.splice(i, 1);
  }

  onCaseStudyChildSelect(event: any, cs: CaseStudyForm): void {
    const child: FullChild = event.value;
    if (!child) return;

    cs.childCode = child.childCode;
    cs.name = `${child.firstName || ''} ${child.lastName || ''}`.trim();
    cs.gender = child.gender || '';
    // Not every child record has `age` filled in; fall back to working it out from the DOB.
    cs.age = Number(child.age) || this.ageFromDob(child.dob);
    cs.dob = this.formatChildDob(child.dob);
    cs.city = child.city || '';
    cs.state = child.state || '';
    cs.diagnosis = child.diagnosis || (child.diagnosisSelected && child.diagnosisSelected[0]) || '';
    cs.hospital = child.hospital || '';
    if (!cs.braveheartLabel) cs.braveheartLabel = `Braveheart ${cs.name}`;

    const suggestedPhoto = this.getChildPhotoUrl(child.admissionDocuments || []);
    if (suggestedPhoto) {
      cs.photo = suggestedPhoto;
      cs.photoIsSuggested = true;
    }
  }

  /** Completed years since the date of birth, or 0 when the DOB is missing or invalid. */
  private ageFromDob(dob: any): number {
    if (!dob) return 0;
    const d: Date = typeof dob.toDate === 'function' ? dob.toDate() : new Date(dob);
    if (isNaN(d.getTime())) return 0;
    const now = new Date();
    let years = now.getFullYear() - d.getFullYear();
    if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) years--;
    return Math.max(0, years);
  }

  private formatChildDob(dob: any): string {
    if (!dob) return '';
    const d: Date = typeof dob.toDate === 'function' ? dob.toDate() : new Date(dob);
    if (isNaN(d.getTime())) return '';
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  }

  async generateCaseStudyStory(cs: CaseStudyForm): Promise<void> {
    if (!cs.name) {
      this.snackbar.open('Enter the child\'s name first', '', { duration: 3000 });
      return;
    }
    cs.generating = true;
    try {
      const paragraphs = await this.aiService.generateCaseStudy({
        name: cs.name, gender: cs.gender, age: cs.age, city: cs.city, state: cs.state,
        diagnosis: cs.diagnosis, hospital: cs.hospital,
        centreName: this.configForm.value.centreName || this.configForm.value.centreCode,
        backgroundNotes: cs.backgroundNotes,
      });
      if (!paragraphs.length) {
        // Keep whatever the user already had rather than wiping it with an empty reply.
        this.snackbar.open('The AI returned no story text — please try again', '', { duration: 4000 });
        return;
      }
      cs.paragraphs = paragraphs;
      if (!cs.braveheartLabel) cs.braveheartLabel = `Braveheart ${cs.name}`;
    } catch (err: any) {
      // Show the reason (e.g. the AI service being busy) rather than a bare "failed".
      this.snackbar.open(`Story generation failed: ${err?.message || 'please try again'}`, 'OK', { duration: 7000, panelClass: 'error' });
    } finally {
      cs.generating = false;
    }
  }

  addCaseStudyParagraph(cs: CaseStudyForm): void { cs.paragraphs.push(''); }
  removeCaseStudyParagraph(cs: CaseStudyForm, i: number): void { cs.paragraphs.splice(i, 1); }

  async uploadCaseStudyPhoto(event: any, cs: CaseStudyForm): Promise<void> {
    const file: File = event.target.files?.[0];
    if (!file) return;
    cs.uploadingPhoto = true;
    try {
      const urls = await this.uploadPhotosBatch([file], `narrative-impact-reports/${this.getWorkingReportId()}/case-studies/${cs.childCode || cs.name || 'child'}`);
      cs.photo = urls[0];
      cs.photoIsSuggested = false;
    } finally {
      cs.uploadingPhoto = false;
      event.target.value = '';
    }
  }

  // ── Step 6: Generate PDF ───────────────────────────────────────────────────

  async generateAndPrint(): Promise<void> {
    if (this.configForm.invalid) {
      this.snackbar.open('Complete Step 1 (centre, year, quarter) first', '', { duration: 3000 });
      return;
    }
    this.generatingPdf = true;
    this.loader.setLoading(true);

    try {
      const reportData = this.buildReportData();

      const fn = this.functions.httpsCallable('generateNarrativeImpactReport');
      const result: any = await fn(reportData).toPromise();

      if (result?.html) {
        this.previewHtml = result.html;
        await this.persistReport(this.getWorkingReportId(), reportData);
        this.snackbar.open('Report ready — review below, then click Print / Save as PDF', '', { duration: 5000 });
        setTimeout(() => this.writePreviewFrame(result.html), 80);
      }
    } catch (err: any) {
      console.error(err);
      this.snackbar.open('Failed to generate report: ' + (err?.message || ''), '', { duration: 4000, panelClass: 'error' });
    } finally {
      this.generatingPdf = false;
      this.loader.setLoading(false);
    }
  }

  printReport(): void {
    const frame = document.getElementById('narrativeReportPreviewFrame') as HTMLIFrameElement;
    if (!frame?.contentWindow) return;
    frame.contentWindow.print();
  }

  private writePreviewFrame(html: string): void {
    const frame = document.getElementById('narrativeReportPreviewFrame') as HTMLIFrameElement;
    if (!frame) return;
    frame.contentDocument!.open();
    frame.contentDocument!.write(html);
    frame.contentDocument!.close();
  }

  // ── Save to Firestore ──────────────────────────────────────────────────────

  async saveReport(): Promise<void> {
    if (this.configForm.invalid) {
      this.snackbar.open('Complete Step 1 (centre, year, quarter) before saving', '', { duration: 3000 });
      return;
    }
    this.saving = true;
    try {
      const docId = this.getWorkingReportId();
      await this.persistReport(docId, this.buildReportData());
      this.snackbar.open('Report saved', 'OK', { duration: 3000 });
    } catch (err: any) {
      this.snackbar.open('Save failed: ' + (err?.message || ''), '', { duration: 4000 });
    } finally {
      this.saving = false;
    }
  }

  private async persistReport(id: string, data: any): Promise<void> {
    const q = this.selectedQuarter!;
    await this.reportService.updateReport(id, {
      ...data,
      centreCode: this.configForm.value.centreCode,
      from: q.start,
      to: q.end,
      submitType: 'draft',
      createdBy: '',
    } as any);
    this.reportId = id;
    this.savedAt = new Date();
  }

  private buildReportData(): any {
    const sv = this.statsForm.value;
    const eduSv = this.eduForm.value;
    const cslSv = this.cslForm.value;
    const cfg = this.configForm.value;

    return {
      centreName: cfg.centreName || cfg.centreCode,
      quarterLabel: cfg.quarterLabel,
      periodLabel: this.periodLabel,
      coverTitle: cfg.coverTitle,
      watermarkStyle: cfg.watermarkStyle,
      placeholderPhotos: this.selectedPlaceholderPhotos,
      logoUrl: this.logoDataUrl || (window.location.origin + '/assets/logo.png'),
      coverPhotoUrl: this.coverPhotoUrl,
      contactPhotoUrl: this.contactPhotoUrl,
      beneficiaryStats: sv as BeneficiaryStats,
      ageWiseData: this.ageWiseData,
      genderWiseData: this.genderWiseData,
      hospitalWiseData: this.hospitalWiseData,
      beneficiaryRemarks: this.beneficiaryRemarks,
      sections: { ...this.sections },
      education: {
        introText: eduSv.introText,
        ageRangeText: eduSv.ageRangeText,
        toddlerText: eduSv.toddlerText,
        teacherSessions: eduSv.teacherSessions,
        volunteerSessions: eduSv.volunteerSessions,
        specialDaysObserved: eduSv.specialDaysObserved,
        highlights: this.educationHighlights.filter(h => h?.trim()),
        progressChart: [
          { subject: eduSv.langLabel?.trim() || 'Language', baseline: eduSv.langBaseline, discharge: eduSv.langDischarge },
          { subject: eduSv.mathsLabel?.trim() || 'Maths', baseline: eduSv.mathsBaseline, discharge: eduSv.mathsDischarge },
          { subject: eduSv.evsLabel?.trim() || 'EVS/GK', baseline: eduSv.evsBaseline, discharge: eduSv.evsDischarge },
        ] as ProgressSubjectScore[],
        progressNote: eduSv.progressNote,
        headings: eduSv.headings,
        sessionPhotos: this.eduSessionPhotos,
        activityPhotos: this.eduActivityPhotos,
        volunteerPhotos: this.eduVolunteerPhotos,
        // TODO: upload these data URLs via uploadPhotosBatch() (like the
        // raw *Photos arrays above) before this ships — storing a full-size
        // JPEG as an inline base64 string risks the 1MB Firestore doc cap.
        // Firestore rejects `undefined` field values (see ALCoverPhoto's
        // addCoverPhoto() for the same convention), so these keys are only
        // spread in when actually set rather than assigned `|| undefined`.
        ...(this.eduSessionCollageUrl ? { sessionCollageUrl: this.eduSessionCollageUrl, sessionCollageFullPage: this.eduSessionCollageFullPage } : {}),
        ...(this.eduActivityCollageUrl ? { activityCollageUrl: this.eduActivityCollageUrl, activityCollageFullPage: this.eduActivityCollageFullPage } : {}),
        ...(this.eduVolunteerCollageUrl ? { volunteerCollageUrl: this.eduVolunteerCollageUrl, volunteerCollageFullPage: this.eduVolunteerCollageFullPage } : {}),
      },
      counselling: {
        openingQuote: cslSv.openingQuote,
        introParagraphs: (cslSv.introParagraphs || '').split('\n\n').filter((p: string) => p.trim()),
        individualSessions: cslSv.individualSessions,
        psychologicalInterventions: cslSv.psychologicalInterventions,
        groupSessions: cslSv.groupSessions,
        problemsAddressedParagraph: this.problemsAddressedParagraph,
        highlights: this.counsellingHighlights.filter(h => h?.trim()),
        interventionsToolsParagraph: cslSv.interventionsToolsParagraph,
        admissionsDischargeParagraphs: (cslSv.admissionsDischargeParagraphs || '').split('\n\n').filter((p: string) => p.trim()),
        admissionCoping: this.admissionCoping,
        dischargeCoping: this.dischargeCoping,
        // One bullet per line.
        interpretationBullets: (cslSv.interpretationBullets || '').split('\n').map((b: string) => b.trim()).filter(Boolean),
        headings: cslSv.headings,
        closingParagraph: cslSv.closingParagraph,
        sessionPhotos: this.cslSessionPhotos,
        groupPhotos: this.cslGroupPhotos,
        ...(this.cslSessionCollageUrl ? { sessionCollageUrl: this.cslSessionCollageUrl, sessionCollageFullPage: this.cslSessionCollageFullPage } : {}),
        ...(this.cslGroupCollageUrl ? { groupCollageUrl: this.cslGroupCollageUrl, groupCollageFullPage: this.cslGroupCollageFullPage } : {}),
      },
      caseStudies: this.caseStudies.map(cs => ({
        braveheartLabel: cs.braveheartLabel || `Braveheart ${cs.name}`,
        name: cs.name, gender: cs.gender, age: cs.age, dob: cs.dob, city: cs.city, state: cs.state,
        diagnosis: cs.diagnosis, hospital: cs.hospital, photo: cs.photo,
        // Defensive: a non-array or non-string entry (e.g. from an older saved draft) must never
        // drop the whole case study's text or crash report generation.
        paragraphs: (Array.isArray(cs.paragraphs) ? cs.paragraphs : [])
          .map(p => (typeof p === 'string' ? p : '').trim())
          .filter(Boolean),
      })) as CaseStudyData[],
    };
  }

  private async uploadPhotosBatch(files: File[], basePath: string): Promise<string[]> {
    const urls: string[] = [];
    for (const file of files) {
      const path = `${basePath}/${Date.now()}_${file.name}`;
      const ref = this.storage.ref(path);
      await ref.put(file);
      const url = await ref.getDownloadURL().toPromise();
      urls.push(url);
    }
    return urls;
  }

  private getWorkingReportId(): string {
    if (!this.reportId) {
      this.reportId = this.afs.createId();
    }
    return this.reportId;
  }
}
